/**
 * Parse a number string with suffixes like 1.5k, 2m, 3b
 * @param value - String to parse (e.g., "1.5k", "2M", "3B")
 * @returns Parsed number or null if invalid
 */
export const parseNumberWithSuffix = (value: string): number | null => {
  // Remove all spaces and convert to lowercase
  const cleaned = value.replace(/\s+/g, '').toLowerCase();
  
  // Match number followed by optional suffix
  const match = cleaned.match(/^(-?\d*\.?\d+)(k|m|b)?$/);
  if (!match) return null;

  const [, num, suffix] = match;
  const baseValue = parseFloat(num);
  
  if (isNaN(baseValue)) return null;

  switch (suffix) {
    case 'k': return baseValue * 1000;
    case 'm': return baseValue * 1000000;
    case 'b': return baseValue * 1000000000;
    default: return baseValue;
  }
};

/**
 * Format a number with commas for display
 * @param value - Number or string to format
 * @returns Formatted string with commas
 */
export const formatNumberWithCommas = (value: number | string | undefined): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  
  let numValue: number;
  
  if (typeof value === 'string') {
    // Remove any existing commas or non-numeric characters except decimal points
    const cleaned = value.replace(/[^\d.-]/g, '');
    numValue = parseFloat(cleaned);
    if (isNaN(numValue)) {
      return '';
    }
  } else {
    numValue = value;
  }
  
  // Format with commas for thousands - ensure it works with all numbers including zeros
  return numValue.toLocaleString('en-US');
};

/**
 * Format money with compact notation for large amounts
 * @param value - Number to format
 * @returns Formatted currency string
 */
export const formatMoney = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '$0';
  }
  
  const absValue = Math.abs(value);
  
  if (absValue >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  } else if (absValue >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  } else if (absValue >= 1) {
    return `$${value.toLocaleString('en-US')}`;
  }
  
  return `$${value.toFixed(0)}`;
};

/**
 * Validate and parse monetary input with suffix support
 * @param value - Input string to validate and parse
 * @returns Parsed number or undefined if invalid
 */
export const validateMonetaryInput = (value: string): number | undefined => {
  if (!value) return undefined;
  
  // Check if the value has suffixes like k, m, b
  if (value.match(/^(-?\d*\.?\d+)(k|m|b)$/i)) {
    const parsedValue = parseNumberWithSuffix(value);
    return parsedValue !== null ? parsedValue : undefined;
  }
  
  // Otherwise try to parse as regular number
  const numValue = parseFloat(value);
  return !isNaN(numValue) ? numValue : undefined;
};
